<template>
    <div>

        <!-- BUTTONS -->

        <b-button class="btn-sf btn-square" @click="$refs[name].click()" v-if="!this.failed && !this.uploading && !this.uploaded">
            <i class="fal fa-upload"></i> Upload
            <input type="file" class="d-none" :ref="name" :name="name" :accept="accepts" @change="upload">
        </b-button>

        <b-button variant="outline-primary" squared disabled v-if="this.uploading">
            <i class="fal fa-spinner fa-spin"></i> Uploading
        </b-button>

        <b-button-group v-if="this.failed">
            <b-button variant="outline-danger" squared>
                <i class="fal fa-times-circle"></i> Failed
            </b-button>
            <b-button variant="outline-danger" squared :id="'failed-'+_uid">
                <i class="fal fa-info-circle"></i>
                <b-tooltip :target="'failed-'+_uid" triggers="hover">
                    {{ error }}
                </b-tooltip>
            </b-button>
            <b-button variant="outline-danger" squared @click="resetUpload">
                <i class="fal fa-undo"></i>
            </b-button>
        </b-button-group>

        <b-button-group v-if="this.uploaded">
            <b-button variant="outline-success" squared>
                <i class="fal fa-check-circle"></i> Uploaded
            </b-button>
            <b-button v-if="manage_meta" variant="outline-success" squared @click="show_meta = true">
                <i class="fal fa-pencil"></i>
            </b-button>
            <b-button v-if="manage_attachments" variant="outline-success" squared @click="show_attachments = true">
                <i class="fal fa-paperclip"></i>
            </b-button>
            <b-button v-if="manage_permissions" variant="outline-success" class="d-none" squared @click="show_permissions = true">
                <i class="fal fa-lock"></i>
            </b-button>
            <b-button v-if="manage_cover" variant="outline-success" squared @click="show_cover = true">
                <i class="fal fa-image-polaroid"></i>
            </b-button>
            <b-button variant="outline-success" squared @click="resetUpload">
                <i class="fal fa-undo"></i>
            </b-button>
            <b-button v-if="deletable" variant="outline-success" squared @click="deleteDocument">
                <i class="fal fa-trash"></i>
            </b-button>
        </b-button-group>

        <!-- NOTE MODAL -->

        <c-modal v-if="manage_meta" :show.sync="show_meta" title="Update Details" size="lg">
            <b-row>
                <b-col cols="3"><label>Filename</label></b-col>
                <b-col><c-input v-model="meta.name"></c-input></b-col>
            </b-row>
            <b-row>
                <b-col cols="3"><label>Description</label></b-col>
                <b-col><c-textarea v-model="meta.description"></c-textarea></b-col>
            </b-row>
            <template #footer="">
                <c-button v-if="!saving_meta" class="float-right" color="success" @click="saveMeta"><i class="fal fa-save"></i></c-button>
                <c-button v-if="saving_meta" class="float-right" color="default" disabled><i class="fal fa-spin fa-spinner"></i></c-button>
            </template>
        </c-modal>

        <!-- ATTACHMENTS MODAL -->

        <c-modal v-if="manage_attachments" :show.sync="show_attachments" title="Manage Attachments" size="lg">
            <template #footer=""><span></span></template>
        </c-modal>

        <!-- PERMISSIONS MODAL -->

        <c-modal v-if="manage_permissions"  :show.sync="show_permissions" title="Manage Permissions" size="lg">
            <permission-list
                :document="uploaded_document"
                :models="permission_models"
                label_field="name"
                key_field="id"
                :selected="[]"
            ></permission-list>
            <template #footer=""><span></span></template>
        </c-modal>

        <!-- COVER MODAL -->

        <c-modal v-if="manage_cover" :show.sync="show_cover" title="Update Cover Image" size="lg">
            <div v-if="uploaded_document">
                <div v-if="cover_src" class="bg-gray-900 text-center">
                    <img :src="cover_src" height="200">
                </div>
                <div v-else></div>
                <upload-button :pre_loaded_document="uploaded_document.cover" :editable="true" :deletable="false" @onUploaded="(document) => updateCover(document)"></upload-button>
            </div>
            <b-alert v-else show>No document to add a cover image to.</b-alert>
            <template #footer=""><span></span></template>
        </c-modal>

    </div>
</template>

<script>
  import Document from "../../models/Document";
  import ToastNotifications from "../shared/mixins/ToastNotifications";
  import PermissionList from "./PermissionList";

  export default {
    name: 'UploadButton',
    components: {PermissionList},
    mixins: [ToastNotifications],
    props: {
      folder_id: {
        default: function () {
          return null;
        }
      },
      accepts: {
        default: function () {
          return '*';
        }
      },
      pre_loaded_file: {
        default: function () {
          return null;
        }
      },
      pre_loaded_document: {
        default: function () {
          return null;
        }
      },
      manage_meta: {
        default: function () {
          return false;
        }
      },
      manage_attachments: {
        default: function () {
          return false;
        }
      },
      manage_permissions: {
        default: function () {
          return false;
        }
      },
      manage_cover: {
        default: function () {
          return false;
        }
      },
      editable: {
        default: function () {
          return false;
        }
      },
      deletable: {
        default: function () {
          return true;
        }
      },
      attachment_models: {
        default: function () {
          return null;
        }
      },
      permission_models: {
        default: function () {
          return null;
        }
      },
    },
    data() {
      return {
        name: 'file',

        uploaded_file: null,
        uploaded: false,
        uploading: false,
        request: null,
        response: null,
        existing_document: null,
        uploaded_document: null,
        failed: false,
        error: null,

        show_meta: false,
        saving_meta: false,
        saving_cover: false,
        show_attachments: false,
        show_permissions: false,
        show_cover: false,

        cover_src: null,

        meta: {
          document_folder_id: this.folder_id,
          name: null,
          description: null,
        }

      };
    },
    watch: {
      uploading: function() {
        if(this.uploading) {
          this.$emit('onUpload', this.request, this.uploaded_file);
        }
      },
      uploaded: function() {
        if(this.uploaded && this.uploaded_file) {
          this.$emit('onUploaded', this.uploaded_document, this.response, this.uploaded_file);
        }
      },
      failed: function() {
        if(this.failed) {
          this.$emit('onFail', this.error, this.request, this.uploaded_file);
        }
      },
      uploaded_document: function() {
        if(this.manage_cover && this.uploaded_document && this.uploaded_document.cover) {
          this.loadCover();
        } else {
          this.cover_src = null;
        }
        this.$emit('onDocumentChange', this.uploaded_document);
      },
      pre_loaded_document: function() {
        this.existing_document = this.pre_loaded_document;
        this.uploaded_document = this.existing_document;
        this.meta.name = (this.uploaded_document ? this.uploaded_document.name : null);
        this.meta.description = (this.uploaded_document ? this.uploaded_document.description : null);
        this.uploaded = true;
      },
    },
    methods: {
      upload(event, forced_file) {
        this.uploading = true;
        if(forced_file) {
          this.uploaded_file = forced_file;
        } else {
          this.uploaded_file = event.target.files[0];
        }
        let formData = new FormData();
        if(this.folder_id) {
          formData.append('document_folder_id', this.folder_id);
        }
        formData.append(this.name, this.uploaded_file);
        if(this.editable && this.existing_document && this.existing_document.id) {
          if(this.meta.name) {
            formData.append('name', this.meta.name);
          } else {
            formData.append('name', this.existing_document.name);
          }
          if(this.meta.description) {
            formData.append('description', this.meta.description);
          } else {
            formData.append('description', this.existing_document.description);
          }
          formData.append('_method', 'PUT');
          this.request = Document.api().update(this.existing_document.id, formData);
        } else {
          this.request = Document.api().create(formData);
        }
        this.request.then((response) => {
          this.failed = false;
          this.uploading = false;
          this.response = response;
          this.uploaded_document = response.entities[Document.entity][0];
          this.meta.name = this.uploaded_document.name;
          this.meta.description = this.uploaded_document.description;
          if(this.editable) {
            this.existing_document = this.uploaded_document;
          }
          this.uploaded = true;
        }, (err) => {
          this.uploaded = false;
          this.uploading = false;
          this.error = (typeof err == 'string' ? err : (err.message ? err.message : 'Failed'));
          this.failed = true;
        })
      },
      resetUpload() {
        this.uploaded_file = null;
        this.uploaded = false;
        this.uploading = false;
        this.request = null;
        this.response = null;
        this.uploaded_document = null;
        this.failed = false;
        this.error = null;
      },
      saveMeta() {
        if(this.uploaded_document) {
          this.saving_meta = true;
          Document.api().update(this.uploaded_document.id, this.meta).then((response) => {
            this.saving_meta = false;
            this.show_meta = false;
            this.uploaded_document = response.entities[Document.entity][0];
            this.quickSuccessToast('Updated document', 'Your document details have been updated.');
          }, (err) => {
            this.saving_meta = false;
            this.quickWarningToast('Document could not be updated', err.message ? err.message : 'Please try again.');
          });
        }
      },
      deleteDocument() {
        if(this.uploaded_document) {
          const id = this.uploaded_document.id;
          Document.api().remove(id).then(() => {
            Document.delete(id);
            this.resetUpload();
            this.quickSuccessToast('Deleted document', 'Your document was deleted successfully.');
            this.$emit('onDelete', this.uploaded_document)
          });
        }
      },
      loadCover() {
        this.cover_src = null;
        Document.api().file(this.uploaded_document.cover.id, this.uploaded_document.cover.mime_type, true).then((response) => {
          let data = response.response.data;
          let type = this.uploaded_document.cover.mime_type;
          let file = new Blob([data], {type: type});
          let urlCreator = window.URL || window.webkitURL;
          this.cover_src = urlCreator.createObjectURL(file);
        }).catch(() => {});
      },
      updateCover(document) {
        if(this.uploaded_document) {
          Document.api().updateCover(this.uploaded_document.id, document.id).then(() => {
            this.saving_cover = false;
            this.uploaded_document = Document.query().with(['cover']).find(this.uploaded_document.id);
            this.quickSuccessToast('Updated cover', 'Your document cover image has been updated.');
          }, (err) => {
            this.quickWarningToast('Cover image could not be updated', err.message ? err.message : 'Please try again.');
          });
        }
      }
    },
    created() {
      if(this.pre_loaded_document) {
        this.existing_document = this.pre_loaded_document;
        this.uploaded_document = this.existing_document;
        this.meta.name = this.uploaded_document.name;
        this.meta.description = this.uploaded_document.description;
        this.uploaded = true;
      } else if(this.pre_loaded_file) {
        this.upload(null, this.pre_loaded_file);
      }
    }
  }
</script>